import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MONITOR_MODE_DEFS } from '@goyone/shared';
import { api } from '../../lib/api';
import { PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';

/**
 * إعدادات مركز المراقبة.
 *
 * مراقبة الشاشة الدائمة: لقطاتٌ دورية أو فيديو وردية، وجهتهما Google Drive
 * الشركة (يُضبط من الإعدادات ← التكاملات). المشاهدة الحيّة لا تحتاج ضبطًا —
 * تعمل ما دام للموظف جلسةٌ مفتوحة.
 */

interface MonitorCfg {
  callRecording: boolean;
  mode: 'OFF' | 'SCREENSHOTS' | 'VIDEO';
  screenshotEverySec: number;
  retentionDays: number;
  storage: 'DB' | 'DRIVE';
  driveReady: boolean;
}

export function MonitorSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['cc', 'monitoring'],
    queryFn: () => api<MonitorCfg>('/callcenter/settings/monitoring'),
  });

  const [mode, setMode] = useState<'OFF' | 'SCREENSHOTS' | 'VIDEO'>('OFF');
  const [everySec, setEverySec] = useState('60');
  const [retention, setRetention] = useState('7');
  const [storage, setStorage] = useState<'DB' | 'DRIVE'>('DB');

  useEffect(() => {
    const c = config.data;
    if (!c) return;
    setMode(c.mode);
    setEverySec(String(c.screenshotEverySec));
    setRetention(String(c.retentionDays));
    setStorage(c.storage);
  }, [config.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/callcenter/settings/monitoring', {
        method: 'PATCH',
        body: {
          mode,
          screenshotEverySec: Number(everySec) || 60,
          retentionDays: Number(retention) || 7,
          storage,
        },
      }),
    onSuccess: () => {
      toast('حُفظت إعدادات المراقبة');
      void queryClient.invalidateQueries({ queryKey: ['cc', 'monitoring'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const driveReady = config.data?.driveReady ?? false;

  return (
    <div className="page">
      <PageHead
        title="إعدادات المراقبة"
        subtitle="وضع المراقبة، ومدة الاحتفاظ، ومكان التخزين"
      />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">مراقبة الشاشة</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <label className="field">
              <span className="label">وضع المراقبة</span>
              <select
                className="select"
                value={mode}
                onChange={(e) => setMode(e.target.value as typeof mode)}
              >
                {MONITOR_MODE_DEFS.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>

            {mode === 'SCREENSHOTS' && (
              <label className="field">
                <span className="label">فاصل اللقطة (ثانية)</span>
                <input
                  className="input num"
                  type="number"
                  min={10}
                  max={600}
                  value={everySec}
                  onChange={(e) => setEverySec(e.target.value)}
                />
                <span className="tiny mute">بين ١٠ و٦٠٠ ثانية.</span>
              </label>
            )}

            <label className="field">
              <span className="label">مدة الاحتفاظ (يوم)</span>
              <input
                className="input num"
                type="number"
                min={1}
                max={90}
                value={retention}
                onChange={(e) => setRetention(e.target.value)}
              />
              <span className="tiny mute">
                يُحذف الملتقَط تلقائيًّا بعدها — الفيديو أسبوعٌ عادةً.
              </span>
            </label>

            <label className="field">
              <span className="label">مكان التخزين</span>
              <select
                className="select"
                value={storage}
                onChange={(e) => setStorage(e.target.value as 'DB' | 'DRIVE')}
              >
                <option value="DB">في النظام</option>
                <option value="DRIVE" disabled={!driveReady}>
                  Google Drive الشركة{driveReady ? '' : ' (غير مربوط)'}
                </option>
              </select>
              <span className="tiny mute">
                {storage === 'DRIVE'
                  ? 'يُرفع الثقيل إلى Drive ويبقى في النظام مؤشّرٌ إليه.'
                  : 'يُحفظ في قاعدة النظام ويُعرض فيه مباشرة.'}
              </span>
            </label>
          </div>

          {/*
            Drive صار اختيارًا لا شرطًا.
            المراقبة تعمل بالتخزين في النظام؛ وDrive للتخفيف حين يُربط، ورابطه
            في الإعدادات ← التكاملات.
          */}
          <div className="alert alert-info mt-3">
            <span>
              {driveReady ? (
                'Drive مربوط — يمكنك تفريغ الثقيل إليه بجعل مكان التخزين «Google Drive».'
              ) : (
                <>
                  التخزين في النظام يعمل بلا تكامل. ولتفريغ الثقيل إلى Drive،{' '}
                  <Link to="/settings/drive" className="link">
                    اربطه من الإعدادات ← التكاملات
                  </Link>
                  .
                </>
              )}
            </span>
          </div>

          <div className="alert alert-info mt-3">
            المراقبة شفافة بالبنية: المتصفح يطلب إذن الموظف بيده كل جلسة، ويُبقي مؤشّر
            المشاركة ظاهرًا طوالها — لا التقاط خفيّ. وإعلام الموظفين بالسياسة مسؤوليتكم
            قانونًا.
          </div>

          <button
            className="btn btn-primary mt-3"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            حفظ
          </button>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">المشاهدة الحيّة</h2>
        </div>
        <div className="card-body">
          <p className="small">
            لا تحتاج ضبطًا: افتح{' '}
            <Link to="/monitor" className="link">
              مركز المراقبة
            </Link>{' '}
            واختر موظفًا في جلسة نشطة لترى شاشته الآن. تعمل ما دام للموظف جلسة تسجيل
            مفتوحة، بلا علاقة بوضع المراقبة الدائمة أعلاه.
          </p>
        </div>
      </div>
    </div>
  );
}
